// =============================================================================
// DiarySpaceModal.tsx — T-330. Manager+ generates a hosted client diary SPACE
// (a gated portal "library") for a project: pick a title, tick which entries
// are included, Generate → an unguessable token + 7-day expiry. Output is a
// shareable LINK only (copy / open) — no email. Regenerating revokes earlier
// live links so no backlog builds. Public page = /diary-view/:token.
// =============================================================================

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X, Share2, Link2, Copy, Check, CheckCircle2, AlertTriangle,
  CalendarClock, BookOpen, ExternalLink, Trash2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button } from "@/components/ui";
import { format, parseISO } from "date-fns";

export interface SpaceEntry {
  diary_id:    string;
  entry_date:  string;
  scope_done:  string | null;
  photo_count: number;
  has_delay:   boolean;
}

interface Props {
  projectId: string;
  entries:   SpaceEntry[];
  onClose:   () => void;
}

const EXPIRY_DAYS = 20;   // T-330 — project-length link; each update/extend pushes it out 20 more days

function mintToken(): string {
  const a = crypto.randomUUID().replace(/-/g, "");
  const b = crypto.randomUUID().replace(/-/g, "");
  return (a + b).slice(0, 40);
}

export default function DiarySpaceModal({ projectId, entries, onClose }: Props) {
  const user = useRequireAuth();

  const [title, setTitle]       = useState("");
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [copied, setCopied]     = useState(false);
  const [result, setResult]     = useState<{ url: string; expires_at: string } | null>(null);

  const { data: project } = useQuery({
    queryKey: ["diary_space_project", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_code, project_name, client_id, clients ( legal_name )")
        .eq("project_id", projectId)
        .single();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: shares = [], refetch: refetchShares } = useQuery({
    queryKey: ["diary_spaces", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_diary_shares")
        .select("share_id, token, title, created_at, expires_at, revoked_at, view_count")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  async function revoke(shareId: string) {
    await supabase.from("site_diary_shares").update({ revoked_at: new Date().toISOString() }).eq("share_id", shareId);
    refetchShares();
  }

  // Push the expiry out another EXPIRY_DAYS without touching the entries — the
  // "extend the token" James asked for, straight from the portal manager.
  async function extend(shareId: string) {
    const expires = new Date(Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString();
    await supabase.from("site_diary_shares").update({ expires_at: expires }).eq("share_id", shareId);
    refetchShares();
  }

  const includedIds = useMemo(
    () => entries.filter(e => !excluded.has(e.diary_id)).map(e => e.diary_id),
    [entries, excluded],
  );
  const includedCount = includedIds.length;
  const totalPhotos = useMemo(
    () => entries.filter(e => !excluded.has(e.diary_id)).reduce((a, e) => a + e.photo_count, 0),
    [entries, excluded],
  );
  const defaultTitle = project ? `${project.project_code} — Site Diary` : "Site Diary";
  const hasLiveShare = shares.some((s: any) => !s.revoked_at && (!s.expires_at || new Date(s.expires_at).getTime() > Date.now()));

  async function generate() {
    setBusy(true); setError(null);
    try {
      if (includedIds.length === 0) throw new Error("Include at least one diary entry.");
      const expires = new Date(Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString();

      // One persistent link per project: reuse the live share if there is one,
      // so the client's saved link keeps working as you add entries. Only mint
      // a fresh token when none exists.
      const { data: lives } = await supabase
        .from("site_diary_shares")
        .select("share_id, token")
        .eq("project_id", projectId)
        .is("revoked_at", null)
        .order("created_at", { ascending: false });
      const liveList = (lives ?? []) as any[];

      let token: string, shareId: string;
      if (liveList.length > 0) {
        token = liveList[0].token; shareId = liveList[0].share_id;
        // Collapse any older live links into the one we keep.
        const extra = liveList.slice(1).map((s) => s.share_id);
        if (extra.length) await supabase.from("site_diary_shares").update({ revoked_at: new Date().toISOString() }).in("share_id", extra);
        await supabase.from("site_diary_shares").update({ title: title.trim() || defaultTitle, expires_at: expires }).eq("share_id", shareId);
      } else {
        token = mintToken();
        const { data: share, error: shErr } = await supabase
          .from("site_diary_shares")
          .insert({ project_id: projectId, token, title: title.trim() || defaultTitle, created_by: user?.user_id ?? null, expires_at: expires })
          .select("share_id")
          .single();
        if (shErr || !share) throw new Error(shErr?.message ?? "Couldn't create the portal — permission?");
        shareId = (share as any).share_id;
      }

      // Sync the entry list: add newly-included, remove any deselected.
      const { data: existing } = await supabase.from("site_diary_share_entries").select("diary_id").eq("share_id", shareId);
      const have = new Set<string>((existing ?? []).map((x: any) => x.diary_id));
      const toAdd = includedIds.filter((id) => !have.has(id));
      const toRemove = [...have].filter((id) => !includedIds.includes(id));
      if (toAdd.length) {
        const { error: addErr } = await supabase.from("site_diary_share_entries").insert(toAdd.map((diary_id) => ({ share_id: shareId, diary_id })));
        if (addErr) throw new Error(addErr.message);
      }
      if (toRemove.length) {
        await supabase.from("site_diary_share_entries").delete().eq("share_id", shareId).in("diary_id", toRemove);
      }

      setResult({ url: `${window.location.origin}/diary-view/${token}`, expires_at: expires });
      refetchShares();
    } catch (e: any) {
      setError(e?.message ?? "Couldn't update the portal.");
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!result) return;
    try { await navigator.clipboard.writeText(result.url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-ink-card border-t md:border border-line w-full md:max-w-2xl md:rounded-lg max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 size={18} className="text-accent" />
            <h3 className="text-[16px] font-semibold text-text">Client diary portal</h3>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-1.5 rounded-md" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {!result && (
            <>
              {/* Context */}
              <div className="bg-ink-raised border border-line rounded-md p-4">
                <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">Project</div>
                <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-accent text-[14px]">{project?.project_code ?? "…"}</span>
                  <span className="text-[13px] text-text">{project?.project_name}</span>
                </div>
                {project?.clients?.legal_name && (
                  <div className="text-[12px] text-text-muted mt-0.5">Client: {project.clients.legal_name}</div>
                )}
              </div>

              {/* Currently shared links */}
              {shares.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium mb-2">Shared links</div>
                  <div className="space-y-1.5">
                    {shares.map((sh: any) => {
                      const active = !sh.revoked_at && (!sh.expires_at || new Date(sh.expires_at).getTime() > Date.now());
                      const link = `${window.location.origin}/diary-view/${sh.token}`;
                      const daysLeft = sh.expires_at ? Math.ceil((new Date(sh.expires_at).getTime() - Date.now()) / 86400000) : null;
                      const soon = active && daysLeft != null && daysLeft <= 5;
                      return (
                        <div key={sh.share_id} className={`px-3 py-2 rounded-md border ${soon ? "bg-warn/10 border-warn/40" : active ? "bg-ink-raised border-line" : "bg-ink-raised/40 border-line opacity-60"}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="min-w-0">
                              <div className="text-[12px] text-text truncate">{sh.title ?? "Site Diary"}</div>
                              <div className={`text-[11px] ${soon ? "text-warn" : "text-text-muted"}`}>
                                {active
                                  ? (daysLeft != null
                                      ? `Live · expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"} (${format(parseISO(sh.expires_at), "dd MMM")})`
                                      : "Live")
                                  : sh.revoked_at ? "Replaced" : "Expired"}
                                {typeof sh.view_count === "number" ? ` · ${sh.view_count} view${sh.view_count === 1 ? "" : "s"}` : ""}
                              </div>
                            </div>
                            {active && (
                              <div className="flex items-center gap-2 flex-shrink-0 text-[12px]">
                                <button type="button" onClick={() => extend(sh.share_id)} className="text-text-muted hover:text-accent inline-flex items-center gap-1"><CalendarClock size={12} /> +{EXPIRY_DAYS}d</button>
                                <button type="button" onClick={() => window.open(link, "_blank", "noopener")} className="text-accent hover:underline inline-flex items-center gap-1"><ExternalLink size={12} /> Open</button>
                                <button type="button" onClick={() => { navigator.clipboard.writeText(link).catch(() => {}); }} className="text-text-muted hover:text-text inline-flex items-center gap-1"><Copy size={12} /> Copy</button>
                                <button type="button" onClick={() => revoke(sh.share_id)} className="text-red-400 hover:underline inline-flex items-center gap-1"><Trash2 size={12} /> Revoke</button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">Space title</label>
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder={defaultTitle}
                  className="mt-1.5 w-full px-3 py-2.5 bg-ink-raised border border-line rounded-md text-text text-sm outline-none focus:border-accent" />
              </div>

              {/* Entry selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium flex items-center gap-1.5">
                    <BookOpen size={11} /> Entries in this space
                    <span className="text-accent font-mono normal-case tracking-normal">({includedCount}/{entries.length})</span>
                  </div>
                  <div className="flex gap-2 text-[11px]">
                    <button type="button" className="text-text-muted hover:text-text" onClick={() => setExcluded(new Set())}>All</button>
                    <span className="text-line">·</span>
                    <button type="button" className="text-text-muted hover:text-text" onClick={() => setExcluded(new Set(entries.map(e => e.diary_id)))}>None</button>
                  </div>
                </div>
                <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
                  {entries.map(e => {
                    const inc = !excluded.has(e.diary_id);
                    return (
                      <button key={e.diary_id} type="button"
                        onClick={() => { const n = new Set(excluded); if (inc) n.add(e.diary_id); else n.delete(e.diary_id); setExcluded(n); }}
                        className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${inc ? "bg-accent/10 border-accent/40" : "bg-ink-raised border-line hover:border-line-light"}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${inc ? "bg-accent border-accent" : "border-line"}`}>
                            {inc && <Check size={12} className="text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] text-text flex items-center gap-2">
                              {format(parseISO(e.entry_date), "EEE dd MMM yyyy")}
                              {e.has_delay && <span className="text-[10px] text-bid">· delay</span>}
                            </div>
                            <div className="text-[11px] text-text-muted truncate">
                              {e.photo_count} photo{e.photo_count === 1 ? "" : "s"}{e.scope_done ? ` · ${e.scope_done.slice(0, 60)}` : ""}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-text-muted mt-1.5">{totalPhotos} photo{totalPhotos === 1 ? "" : "s"} across the included entries. The link is an open library — anyone with it can view + download.</div>
              </div>

              {error && (
                <div className="p-3 rounded bg-red-900/30 border border-red-900/50 text-[12px] text-red-200 flex items-start gap-2">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /><span>{error}</span>
                </div>
              )}
            </>
          )}

          {/* Success */}
          {result && (
            <div className="py-4 space-y-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto rounded-full bg-ok/15 border border-ok/30 flex items-center justify-center"><CheckCircle2 size={22} className="text-ok" /></div>
                <div className="text-[16px] font-semibold text-text">Diary space ready</div>
                <div className="text-[12px] text-text-muted flex items-center justify-center gap-1.5">
                  <CalendarClock size={12} /> Expires {format(parseISO(result.expires_at), "EEE dd MMM yyyy")} · add entries anytime — the link stays the same
                </div>
              </div>
              <div className="bg-ink-raised border border-line rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5 flex items-center gap-1.5"><Link2 size={11} /> Shareable link</div>
                <input readOnly value={result.url} onFocus={e => e.currentTarget.select()}
                  className="w-full px-3 py-2 bg-ink border border-line rounded text-[12px] text-text font-mono" />
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" icon={copied ? Check : Copy} onClick={copyLink}>{copied ? "Copied" : "Copy link"}</Button>
                  <Button size="sm" variant="secondary" icon={ExternalLink} onClick={() => window.open(result.url, "_blank", "noopener")}>Open link</Button>
                </div>
                <div className="text-[11px] text-text-muted mt-2">Send this to the client once at project start — they save it and check back as you add entries. Paste into Teams or Outlook.</div>
              </div>
              <div className="flex justify-end"><Button variant="secondary" size="sm" onClick={onClose}>Done</Button></div>
            </div>
          )}
        </div>

        {!result && (
          <div className="px-5 py-4 border-t border-line flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button icon={Link2} onClick={generate} disabled={busy || includedCount === 0}>{busy ? "Saving…" : hasLiveShare ? "Update portal" : "Create portal link"}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
